import React, { useState, useMemo } from 'react';
import { Modal, Form, Button, Row, Col, Badge, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './ColorProviderModal.css';
import {
  manufacturerColors,
  allManufacturerColors,
  searchColors,
  manufacturerNames,
  findClosestColor
} from './manufacturerColors';

const ColorProviderModal = ({
  show,
  onHide,
  onColorSelect,
  onUseAsReferenceImage,
  currentColor = null,
  title = "Select Color",
  selectedMode,
  setSelectedMode
}) => {
  const { t } = useTranslation();
  const [selectedManufacturer, setSelectedManufacturer] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedColor, setSelectedColor] = useState(null);

  // Filter colors based on manufacturer and search query
  const filteredColors = useMemo(() => {
    let colors = selectedManufacturer === 'all' 
      ? allManufacturerColors 
      : allManufacturerColors.filter(c => c.manufacturer === selectedManufacturer);
    
    if (searchQuery) {
      colors = searchColors(searchQuery).filter(c => 
        selectedManufacturer === 'all' || c.manufacturer === selectedManufacturer
      );
    }
    
    return colors;
  }, [selectedManufacturer, searchQuery]);

  // Find closest match to current color
  const closestMatch = useMemo(() => {
    if (!currentColor) return null;
    return findClosestColor(currentColor);
  }, [currentColor]);

  const handleColorClick = (color) => {
    setSelectedColor(color);
  };

  const handleApply = () => {
    if (selectedColor && onColorSelect) {
      onColorSelect(selectedColor);
      onHide();
    }
  };

  const handleQuickApply = (color) => {
    if (onColorSelect) {
      onColorSelect(color);
      onHide();
    }
  };

  const handleUseColorAsReference = () => {
    if (!selectedColor || typeof onUseAsReferenceImage !== 'function') {
      return;
    }

    onUseAsReferenceImage(selectedColor.rgb || selectedColor.hex, selectedColor.name);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      className="color-provider-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{title || t('colorProvider.title', 'Select Color')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Mode Selector */}
        {selectedMode && setSelectedMode && (
          <div className="mb-3 pb-3" style={{ borderBottom: "1px solid #444" }}>
            <Form.Label style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="bi bi-cursor-fill" style={{ fontSize: "16px" }}></i>
              {t("itemColor-applyMode", "Apply Color To")}
            </Form.Label>
            <Form.Select
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value)}
              className="mode-selector-dropdown"
              style={{ 
                maxWidth: "300px", 
                fontSize: "14px", 
                padding: "10px 12px",
                paddingRight: "35px",
                cursor: "pointer",
                backgroundColor: "#ffffff",
                color: "#212529",
                border: "1px solid #ced4da",
                backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2718%27 height=%2718%27 viewBox=%270 0 16 16%27%3E%3Cpath fill=%27%23000000%27 d=%27M8 12L2 6h12z%27/%3E%3C/svg%3E')",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
                backgroundSize: "16px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none"
              }}
            >
              <option value="Selected Item">{t("itemColor-selectedItem", "Selected Item")}</option>
              <option value="All Windows">{t("itemColor-allWindows", "All Windows")}</option>
              <option value="All Doors">{t("itemColor-allDoors", "All Doors")}</option>
            </Form.Select>
          </div>
        )}

        {/* Search and Filter Controls */}
        <Row className="mb-3 align-items-center">
          <Col md={5}>
            <Form.Group>
              <Form.Label className="fw-bold" style={{ fontSize: "14px", display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <i className="bi bi-building" style={{ fontSize: "16px" }}></i>
                {t('colorProvider.manufacturer', 'Select Manufacturer')}
              </Form.Label>
              <Form.Select
                value={selectedManufacturer}
                onChange={(e) => setSelectedManufacturer(e.target.value)}
                className="manufacturer-selector-dropdown"
                style={{ 
                  fontSize: "14px", 
                  padding: "10px 12px",
                  paddingRight: "35px",
                  cursor: "pointer",
                  backgroundColor: "#ffffff",
                  color: "#212529",
                  border: "1px solid #ced4da",
                  backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2718%27 height=%2718%27 viewBox=%270 0 16 16%27%3E%3Cpath fill=%27%23000000%27 d=%27M8 12L2 6h12z%27/%3E%3C/svg%3E')",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 10px center",
                  backgroundSize: "16px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none"
                }}
              >
                <option value="all">{t('colorProvider.allManufacturers', 'All Manufacturers')}</option>
                {manufacturerNames.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={7}>
            <Form.Group>
              <Form.Label className="fw-bold" style={{ fontSize: "14px", display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <i className="bi bi-search" style={{ fontSize: "16px" }}></i>
                {t('colorProvider.search', 'Search Colors')}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={t('colorProvider.searchPlaceholder', 'Search by name, code, or manufacturer...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ fontSize: "14px", padding: "10px 12px" }}
                />
                {searchQuery && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setSearchQuery('')}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>

        {/* Current Color and Closest Match Info */}
        {currentColor && closestMatch && (
          <div className="current-color-info mb-3 p-3 bg-light rounded">
            <Row>
              <Col md={6}>
                <div className="d-flex align-items-center">
                  <div
                    className="color-swatch me-3"
                    style={{ 
                      backgroundColor: currentColor, 
                      width: '50px', 
                      height: '50px',
                      border: '2px solid #dee2e6',
                      borderRadius: '4px'
                    }}
                  />
                  <div>
                    <strong>{t('colorProvider.currentColor', 'Current Color')}:</strong>
                    <br />
                    <small className="text-muted">{currentColor}</small>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex align-items-center">
                  <div
                    className="color-swatch me-3"
                    style={{ 
                      backgroundColor: closestMatch.rgb, 
                      width: '50px', 
                      height: '50px',
                      border: '2px solid #dee2e6',
                      borderRadius: '4px'
                    }}
                  />
                  <div>
                    <strong>{t('colorProvider.closestMatch', 'Closest Match')}:</strong>
                    <br />
                    <small>{closestMatch.name}</small>
                    <br />
                    <Badge bg="secondary" className="me-1">{closestMatch.code}</Badge>
                    <Badge bg="info">{closestMatch.manufacturerName || closestMatch.manufacturer}</Badge>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Color Count */}
        <div className="mb-2 text-muted small">
          {t('colorProvider.showing', 'Showing')} {filteredColors.length} {t('colorProvider.colors', 'colors')}
        </div>

        {/* Colors Display */}
        <div className="color-selection-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <div className="color-grid">
            {filteredColors.map((color, index) => (
              <div
                key={`${color.manufacturer}-${color.code}-${index}`}
                className={`color-grid-item ${selectedColor?.code === color.code ? 'selected' : ''}`}
                onClick={() => handleColorClick(color)}
                onDoubleClick={() => handleQuickApply(color)}
                title={`${color.name} (${color.code}) - ${color.manufacturerName || color.manufacturer}`}
              >
                <div
                  className="color-swatch"
                  style={{ backgroundColor: color.rgb }}
                />
                <div className="color-info">
                  <div className="color-name">{color.name}</div>
                  <div className="color-code">{color.code}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Selected Color Preview */}
        {selectedColor && (
          <div className="selected-color-preview mt-3 p-3 bg-light rounded">
            <Row className="align-items-center">
              <Col md={2}>
                <div
                  className="color-swatch-large"
                  style={{ 
                    backgroundColor: selectedColor.rgb,
                    width: '80px',
                    height: '80px',
                    border: '2px solid #dee2e6',
                    borderRadius: '4px'
                  }}
                />
              </Col>
              <Col md={7}>
                <h5 className="mb-2">{selectedColor.name}</h5>
                <div className="mb-1">
                  <Badge bg="secondary" className="me-2">{selectedColor.code}</Badge>
                  <Badge bg="info" className="me-2">
                    {selectedColor.manufacturerName || selectedColor.manufacturer}
                  </Badge>
                </div>
                <div className="text-muted small">
                  <div>RGB: {selectedColor.rgb}</div>
                  <div>HEX: {selectedColor.hex}</div>
                </div>
              </Col>
              <Col md={3} className="text-end">
                <div className="selected-color-actions">
                  <Button variant="outline-success" onClick={handleUseColorAsReference}>
                    {t('colorProvider.useAsReferenceImage', 'Use as reference image')}
                  </Button>
                  <Button variant="primary" onClick={handleApply}>
                    {t('colorProvider.applyColor', 'Apply This Color')}
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('colorProvider.cancel', 'Cancel')}
        </Button>
        <Button 
          variant="outline-success"
          onClick={handleUseColorAsReference}
          disabled={!selectedColor}
        >
          {t('colorProvider.useAsReferenceImage', 'Use as reference image')}
        </Button>
        <Button 
          variant="primary" 
          onClick={handleApply}
          disabled={!selectedColor}
        >
          {t('colorProvider.apply', 'Apply')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ColorProviderModal;
